/**
 * Phase 21 — project packages for the wizard's project-type step.
 *
 * Shared by web and mobile so the two surfaces cannot drift (FR-021):
 * both fetch the same reduced card list and both apply through the same
 * endpoint.
 *
 * Note this step does NOT drive a Stella chat turn the way the other
 * steps do — applying a package is a deterministic bulk write, so the
 * caller awaits the mutation and then refetches, rather than waiting for
 * the chat's `streaming → idle` settle hook.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  applyEstimatePackage,
  getEstimate,
  listEstimatePackages,
  patchEstimate,
} from "../api/sdk.gen";
import type {
  Estimate,
  PackageApplyResult,
  PackageCard,
} from "../api/types.gen";

export interface UsePackagesResult {
  /** Project-type cards for this estimate; empty when none apply. */
  packages: PackageCard[];
  /** Which package is currently applied, if any (FR-020). */
  appliedPackageId: string | null;
  /** True while the first fetch is in flight. */
  loading: boolean;
  /** True when this estimate should show the project-type step. */
  hasProjectTypes: boolean;
  /** Re-read the list (e.g. after an apply). */
  refetch: () => void;
}

/**
 * Load the project-type choices for one estimate.
 *
 * The endpoint already scopes by the estimate's room type, so an empty
 * list is the signal to show no step at all — the caller does not need a
 * room-type check of its own.
 */
export function usePackages(estimateId: string | null): UsePackagesResult {
  const [packages, setPackages] = useState<PackageCard[]>([]);
  const [appliedPackageId, setAppliedPackageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  // Ignore a resolved fetch for a previous estimate (or after unmount).
  const activeIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeIdRef.current = estimateId;
    if (!estimateId) {
      setPackages([]);
      setAppliedPackageId(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const settleEmpty = () => {
      if (cancelled || activeIdRef.current !== estimateId) return;
      setLoading(false);
      // A failed fetch must not strand the customer on an empty step —
      // treat it as "no packages" so the original five-step flow
      // continues rather than showing a broken sixth step.
      setPackages([]);
      setAppliedPackageId(null);
    };
    void listEstimatePackages({ estimateId })
      .then((res) => {
        if (cancelled || activeIdRef.current !== estimateId) return;
        if ("error" in res) {
          settleEmpty();
          return;
        }
        setLoading(false);
        setPackages(res.data ?? []);
        const applied = res.meta?.applied_package_id;
        setAppliedPackageId(typeof applied === "string" ? applied : null);
      })
      // A throw (offline, or the client not configured yet) must degrade
      // the same way an error response does — never an unhandled
      // rejection that surfaces as a crash in the host app.
      .catch(settleEmpty);
    return () => {
      cancelled = true;
    };
  }, [estimateId, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return {
    packages,
    appliedPackageId,
    loading,
    hasProjectTypes: packages.length > 0,
    refetch,
  };
}

/** Outcome of applying a package, normalised for the UI. */
export interface ApplyPackageOutcome {
  ok: boolean;
  result?: PackageApplyResult;
  /** Customer-facing message when the apply failed. */
  errorMessage?: string;
}

/**
 * Apply a package to an estimate.
 *
 * Succeeds even when the estimate still needs a measurement — in that
 * case `result.missing_inputs` names the axis and the selections are
 * applied anyway, which is what lets the dimensions step ask for exactly
 * what is missing.
 */
export async function applyPackage(
  estimateId: string,
  packageId: string,
): Promise<ApplyPackageOutcome> {
  const res = await applyEstimatePackage({ estimateId, packageId });
  if ("error" in res) {
    return {
      ok: false,
      errorMessage:
        res.error?.error?.message ??
        "We couldn't start from that package. Please try again.",
    };
  }
  return { ok: true, result: res.data };
}

/**
 * True when the card is the "Other / something else" catch-all rather
 * than a real package.
 *
 * Unlike the intent picker's heuristic, this one is exact: the catch-all
 * is a synthetic card this module owns, so it carries a reserved id.
 */
export const OTHER_PROJECT_TYPE_ID = "__other__";

export function isOtherProjectType(packageId: string): boolean {
  return packageId === OTHER_PROJECT_TYPE_ID;
}

/**
 * Stamp the estimate's `room_type` from the tapped intent card.
 *
 * Phase 21 found this the hard way: the project-type step is gated on
 * `estimate.room_type`, but nothing sets that field deterministically —
 * it depends on Stella emitting `[[IDENTITY:room_type=…]]` during the
 * intent turn, and she does not always. One observed session tapped
 * "Bathroom" and ended with `room_label="Small bathroom remodel"` but
 * `room_type=null`, so the bathroom step never appeared and the customer
 * was dropped into the per-category material chat instead.
 *
 * The intent card already carries the tenant's configured
 * `default_room_type`, so the client can simply say what the customer
 * chose. Stella's directive still runs and still sets `room_label`; this
 * only guarantees the structured field exists.
 *
 * Deliberately non-destructive: if `room_type` is already set (by Stella,
 * or by a previous tap), this leaves it alone. Best-effort — a failure
 * must not block the intent turn, so it resolves false rather than
 * throwing.
 *
 * Returns true when it actually wrote a value.
 */
export async function ensureRoomType(
  estimateId: string,
  roomType: string | null | undefined,
): Promise<boolean> {
  if (!roomType) return false;
  try {
    const current = await getEstimate({ estimateId });
    if ("error" in current) return false;
    if (current.data.room_type) return false; // already known — don't clobber
    const patched = await patchEstimate({
      estimateId,
      ifMatchVersion: current.data.version,
      body: { room_type: roomType as Estimate["room_type"] },
    });
    return !("error" in patched);
  } catch {
    return false;
  }
}
